/**
 * SearchInput Component - 오른쪽 돋보기 아이콘이 있는 검색 입력
 *
 *   <SearchInput
 *     value={q}
 *     onChange={(e) => setQ(e.target.value)}
 *     onSearch={handleSearch}            // Enter 또는 돋보기 클릭 시 실행 (없으면 돋보기는 장식)
 *     onClear={() => setQ('')}           // 넣으면 값이 있을 때 × 버튼 표시
 *     placeholder="검색어"
 *   />
 *
 * - size: sm | md(기본)
 * - loading: 검색 중 - 돋보기 자리에 회전 스피너, 돋보기 클릭 · Enter 막음 (입력은 가능)
 *     <SearchInput value={q} onChange={…} onSearch={run} loading={searching} />
 * - 그 외 <input> 속성(disabled, placeholder, onKeyDown 등) 그대로 전달
 */

import { InputHTMLAttributes, KeyboardEvent } from 'react';
import SearchIcon from '../../assets/icons/common/search.svg?react';
import SpinnerIcon from '../../assets/icons/common/spinner.svg?react';

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  onSearch?: () => void;
  onClear?: () => void;
  size?: 'sm' | 'md' | 'lg';
  /** 검색 중 표시 (돋보기 → 스피너, 중복 실행 막음) */
  loading?: boolean;
}

export function SearchInput({
  onSearch,
  onClear,
  size = 'md',
  loading = false,
  className = '',
  value,
  disabled,
  onKeyDown,
  ...props
}: SearchInputProps) {
  const showClear = !!onClear && !!value && !disabled && !loading;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    // 한글 조합 중 Enter 는 무시 (조합 완료 Enter 와 중복 실행 방지) / 검색 중이면 무시
    if (e.key === 'Enter' && !e.nativeEvent.isComposing && !loading) onSearch?.();
  };

  return (
    <div
      className={[
        'search-input',
        `search-input--${size}`,
        showClear ? 'has-clear' : '',
        loading ? 'is-loading' : '',
        className,
      ].join(' ').trim()}
      aria-busy={loading || undefined}
    >
      <input type="text" value={value} disabled={disabled} onKeyDown={handleKeyDown} {...props} />

      {showClear && (
        <button type="button" className="search-input-clear" onClick={onClear} aria-label="검색어 지우기">
          ×
        </button>
      )}

      {onSearch ? (
        <button
          type="button"
          className="search-input-icon"
          onClick={onSearch}
          disabled={disabled || loading}
          aria-label={loading ? '검색 중' : '검색'}
        >
          {loading ? <SpinnerIcon className="search-input-spinner" /> : <SearchIcon />}
        </button>
      ) : (
        <span className="search-input-icon" aria-hidden="true">
          <SearchIcon />
        </span>
      )}
    </div>
  );
}

export default SearchInput;
